// =======================
// src/componentes/perfil/DadosUsuario.js
// =======================

import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import { api } from "@api/api";
import {
  coresTextosGlobais, tamanhosModaisGlobais,
  tamanhosPaginasAdministrativas,
} from "../../estilos/global";

const obterMensagemErro = (resultado, mensagemPadrao) => {
  const mensagem = resultado?.message ?? resultado?.msg;

  if (Array.isArray(mensagem)) {
    return mensagem.join(" ");
  }

  return mensagem || mensagemPadrao;
};

const emailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function DadosUsuario({ perfil, onPerfilAtualizado }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");

  const [salvando, setSalvando] = useState(false);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  // =======================================================
  // CARREGA OS DADOS ATUAIS
  // =======================================================

  useEffect(() => {
    setNome(perfil?.nome ?? "");
    setEmail(perfil?.email ?? "");
  }, [perfil?.nome, perfil?.email]);

  // =======================================================
  // VALORES NORMALIZADOS
  // =======================================================

  const nomeOriginal = String(perfil?.nome ?? "").trim();

  const emailOriginal = String(perfil?.email ?? "")
    .trim()
    .toLowerCase();

  const nomeAtual = nome.trim();

  const emailAtual = email.trim().toLowerCase();

  // =======================================================
  // ALTERAÇÕES
  // =======================================================

  const nomeAlterado = nomeAtual !== nomeOriginal;

  const emailAlterado = emailAtual !== emailOriginal;

  const possuiAlteracao = nomeAlterado || emailAlterado;

  /*
   * O botão deve ficar disponível sempre que
   * houver alguma alteração.
   *
   * A validação dos dados ocorre ao clicar
   * em "Salvar alterações".
   */
  const podeSalvar = possuiAlteracao && !salvando;

  // =======================================================
  // CANCELAR
  // =======================================================

  const cancelarAlteracoes = () => {
    setNome(perfil?.nome ?? "");

    setEmail(perfil?.email ?? "");

    setErro("");
    setSucesso("");
  };

  // =======================================================
  // SALVAR
  // =======================================================

  const salvarAlteracoes = async () => {
    setErro("");
    setSucesso("");

    // =====================================================
    // VALIDAÇÕES
    // =====================================================

    if (!nomeAtual) {
      setErro("Informe o nome completo.");

      return;
    }

    if (!emailAtual) {
      setErro("Informe o e-mail institucional.");

      return;
    }

    if (!emailValido(emailAtual)) {
      setErro("Informe um e-mail institucional válido.");

      return;
    }

    if (!possuiAlteracao) {
      return;
    }

    setSalvando(true);

    let nomeFoiAtualizado = false;

    try {
      // ===================================================
      // ALTERAÇÃO DO NOME
      // ===================================================

      if (nomeAlterado) {
        const responseNome = await fetch(api.updateProfileName(), {
          method: "PATCH",

          credentials: "include",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            nome: nomeAtual,
          }),
        });

        const resultadoNome = await responseNome.json().catch(() => null);

        if (!responseNome.ok) {
          throw new Error(
            obterMensagemErro(
              resultadoNome,
              "Não foi possível alterar o nome.",
            ),
          );
        }

        const nomeSalvo = resultadoNome?.nome ?? nomeAtual;

        setNome(nomeSalvo);

        onPerfilAtualizado?.({
          nome: nomeSalvo,
        });

        nomeFoiAtualizado = true;
      }

      // ===================================================
      // SOLICITAÇÃO DE ALTERAÇÃO DO E-MAIL
      // ===================================================

      if (emailAlterado) {
        const responseEmail = await fetch(api.requestProfileEmailChange(), {
          method: "POST",

          credentials: "include",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            novoEmail: emailAtual,
          }),
        });

        const resultadoEmail = await responseEmail.json().catch(() => null);

        if (!responseEmail.ok) {
          throw new Error(
            obterMensagemErro(
              resultadoEmail,
              "Não foi possível solicitar a alteração do e-mail.",
            ),
          );
        }

        /*
         * O e-mail do perfil NÃO deve ser atualizado
         * neste momento.
         *
         * O e-mail atual permanece válido até que
         * o usuário confirme a alteração pelo link
         * recebido no endereço atualmente cadastrado.
         */
        setEmail(perfil?.email ?? "");

        setSucesso(
          nomeFoiAtualizado
            ? "Nome atualizado. A confirmação da alteração de e-mail foi enviada para o seu e-mail atual."
            : resultadoEmail?.msg ||
                "A confirmação da alteração de e-mail foi enviada para o seu e-mail atual.",
        );

        return;
      }

      // ===================================================
      // SOMENTE NOME
      // ===================================================

      if (nomeFoiAtualizado) {
        setSucesso("Nome atualizado com sucesso.");
      }
    } catch (error) {
      setErro(
        error instanceof Error
          ? error.message
          : "Não foi possível salvar as alterações.",
      );

      /*
       * Se o nome foi salvo antes de ocorrer
       * um eventual erro na solicitação do e-mail,
       * informamos corretamente o resultado parcial.
       */
      if (nomeFoiAtualizado) {
        setSucesso("O nome foi atualizado com sucesso.");
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",

        boxSizing: "border-box",

        p: { xs: 1.25, md: 1 },

        border: "1px solid var(--sear-borda, #d4e5f6)",

        borderRadius: 1.5,

        bgcolor: "var(--sear-superficie, #ffffff)",
      }}
    >
      {/* =====================================================
          CABEÇALHO
      ===================================================== */}

      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 0.7,

          mb: 2,
        }}
      >
        <PersonRoundedIcon
          sx={{
            color: "var(--sear-azul, #087cf0)",
            fontSize: 20,
          }}
        />

        <Box>
          <Typography
            sx={{
              color: coresTextosGlobais.tituloSecao,

              fontSize: tamanhosPaginasAdministrativas.tituloSecao,

              fontWeight: 700,

              lineHeight: 1.15,
            }}
          >
            Dados do usuário
          </Typography>

          <Typography
            sx={{
              mt: 0.05,

              color: "var(--sear-texto-secundario, #6b7892)",

              fontSize: tamanhosModaisGlobais.descricao,

              lineHeight: { xs: 1.5, md: 1.2 },
            }}
          >
            Atualize suas informações pessoais e institucionais.
          </Typography>
        </Box>
      </Box>

      {/* =====================================================
          MENSAGENS
      ===================================================== */}

      {erro && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            py: 0,
          }}
        >
          {erro}
        </Alert>
      )}

      {sucesso && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            py: 0,
          }}
        >
          {sucesso}
        </Alert>
      )}

      {/* =====================================================
          CAMPOS
      ===================================================== */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            md: "0.85fr 1.15fr",
          },

          gap: 0.85,
        }}
      >
        <TextField
          fullWidth
          required
          size="small"
          label="Nome completo"
          value={nome}
          onChange={(event) => {
            setNome(event.target.value);

            setErro("");
            setSucesso("");
          }}
          disabled={salvando}
          inputProps={{
            maxLength: 100,
          }}
        />

        <TextField
          fullWidth
          required
          size="small"
          type="email"
          label="E-mail institucional"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);

            setErro("");
            setSucesso("");
          }}
          disabled={salvando}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon
                  sx={{
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: 18,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* =====================================================
          AVISO DE ALTERAÇÃO DO E-MAIL
      ===================================================== */}

      {emailAlterado && (
        <Typography
          sx={{
            mt: 0.4,

            color: "var(--sear-texto-secundario, #66758d)",

            fontSize: tamanhosModaisGlobais.textoAuxiliar,

            lineHeight: 1.25,
          }}
        >
          A confirmação será enviada para o e-mail atualmente cadastrado.
        </Typography>
      )}

      {/* =====================================================
          AÇÕES
      ===================================================== */}

      <Box
        sx={{
          display: "flex",

          justifyContent: "flex-end",

          gap: 0.75,

          mt: 0.65,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={cancelarAlteracoes}
          disabled={salvando || !possuiAlteracao}
          sx={{
            minWidth: 100,
            minHeight: 30,

            textTransform: "none",

            fontSize: tamanhosModaisGlobais.botao,

            fontWeight: 600,
          }}
        >
          Cancelar
        </Button>

        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={
            <SaveRoundedIcon
              sx={{
                fontSize: "17px !important",
              }}
            />
          }
          onClick={salvarAlteracoes}
          disabled={!podeSalvar}
          sx={{
            minWidth: 145,
            minHeight: 30,

            textTransform: "none",

            fontSize: tamanhosModaisGlobais.botao,

            fontWeight: 600,

            bgcolor: "var(--sear-acao-azul, #087cf0)",

            boxShadow: "none",

            "&:hover": {
              bgcolor: "var(--sear-acao-azul-hover, #0767d8)",
              boxShadow: "none",
            },
          }}
        >
          {salvando ? "Salvando..." : "Salvar alterações"}
        </Button>
      </Box>
    </Box>
  );
}
